import { useCallback, useEffect, useRef, useState } from 'react';

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: Event) => void) | null;
}

interface SpeechRecognitionEventLike {
  results: { length: number; [index: number]: { [index: number]: { transcript: string } } };
}

function pickMimeType(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/aac'];
  if (typeof MediaRecorder !== 'undefined' && typeof MediaRecorder.isTypeSupported === 'function') {
    for (const c of candidates) {
      if (MediaRecorder.isTypeSupported(c)) return c;
    }
  }
  return '';
}

export interface AudioRecorderState {
  recording: boolean;
  liveTranscript: string;
  audioBlob: Blob | null;
  durationSec: number;
  error: string | null;
  start: () => Promise<void>;
  stop: () => Promise<Blob | null>;
  reset: () => void;
  isSupported: boolean;
}

export function useAudioRecorder(): AudioRecorderState {
  const [recording, setRecording] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [durationSec, setDurationSec] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const startTimeRef = useRef<number>(0);
  const tickRef = useRef<number | null>(null);

  const isSupported = typeof MediaRecorder !== 'undefined';

  const start = useCallback(async () => {
    setError(null);
    setLiveTranscript('');
    setAudioBlob(null);
    setDurationSec(0);
    chunksRef.current = [];

    if (!isSupported) {
      setError('Dein Browser unterstützt keine Sprachaufnahme.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = pickMimeType();
      const mr = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      // onstop is set dynamically per stop() call so we can resolve the Promise
      // with the assembled blob — see stop() below.
      mr.start();
      mediaRecorderRef.current = mr;

      const SR =
        (window as unknown as { webkitSpeechRecognition?: new () => SpeechRecognitionLike }).webkitSpeechRecognition
        ?? (window as unknown as { SpeechRecognition?: new () => SpeechRecognitionLike }).SpeechRecognition;

      if (SR) {
        const rec = new SR();
        rec.continuous = true;
        rec.interimResults = true;
        rec.lang = 'de-DE';
        rec.onresult = (e) => {
          let text = '';
          for (let i = 0; i < e.results.length; i++) {
            text += e.results[i]![0]!.transcript + ' ';
          }
          setLiveTranscript(text.trim());
        };
        rec.onerror = () => { /* swallow — best-effort */ };
        try {
          rec.start();
          recognitionRef.current = rec;
        } catch {
          // already started or unavailable — ignore
        }
      }

      startTimeRef.current = Date.now();
      tickRef.current = window.setInterval(() => {
        setDurationSec(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 250);

      setRecording(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Mikrofon-Zugriff fehlgeschlagen.');
    }
  }, [isSupported]);

  const stop = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch { /* noop */ }
        recognitionRef.current = null;
      }
      if (tickRef.current !== null) {
        window.clearInterval(tickRef.current);
        tickRef.current = null;
      }

      const mr = mediaRecorderRef.current;
      if (!mr || mr.state === 'inactive') {
        setRecording(false);
        resolve(null);
        return;
      }

      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        setAudioBlob(blob);
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        setRecording(false);
        resolve(blob);
      };
      mr.stop();
    });
  }, []);

  const reset = useCallback(() => {
    setLiveTranscript('');
    setAudioBlob(null);
    setDurationSec(0);
    setError(null);
    chunksRef.current = [];
  }, []);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (tickRef.current !== null) window.clearInterval(tickRef.current);
    };
  }, []);

  return { recording, liveTranscript, audioBlob, durationSec, error, start, stop, reset, isSupported };
}
